import React, { ReactNode, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate } from 'react-router';

import {
  ENVIRONMENT_EXTERNAL,
  ENVIRONMENT_INTERNAL,
  ENVIRONMENT_INTERNAL_EXTERNAL,
} from './common/constants/environment';
import {
  CHANNEL_LIST_PATH,
  CHANNEL_VIEW_PATH,
  DEFAULT_VIEW_PATH,
  SEARCH_AI_VIEW_PATH,
  SEARCH_PATH,
  SEARCH_VIEW_PATH,
  SHARE_VIEW_PATH,
  SINGLE_VIEW_PATH,
} from './common/constants/routePath';

import { RootContainers } from './common/helpers';
import { mountTag, unmountTag } from './common/helpers/dom';
import { isInternalType } from './common/helpers/embedCodeParams';
import store from './common/helpers/store';
import * as commonSelectors from './common/redux/selectors/common';
import { resetStoreAction } from './common/redux/slices/common';

import ThemeConfig from '@/mui/theme';
import ErrorBoundary from './common/components/ErrorBoundary/ErrorBoundary';

type GlobalTriggerProps = {
  children: ReactNode;
};

function GlobalTrigger(props: GlobalTriggerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const configWasLoaded = useSelector(commonSelectors.configWasLoadedSelector);
  const status = useSelector(commonSelectors.statusSelector);
  const search = useSelector(commonSelectors.searchSelector);
  const genAI = useSelector(commonSelectors.genAISelector);
  const js = useSelector(commonSelectors.jsSelector);
  const css = useSelector(commonSelectors.cssSelector);
  const environment = useSelector(commonSelectors.environmentSelector);

  const validEnvironment =
    (isInternalType && [ENVIRONMENT_INTERNAL, ENVIRONMENT_INTERNAL_EXTERNAL].includes(environment)) ||
    (!isInternalType && [ENVIRONMENT_EXTERNAL, ENVIRONMENT_INTERNAL_EXTERNAL].includes(environment));

  const shouldRender = configWasLoaded && status && validEnvironment;

  useLayoutEffect(() => {
    let jsTag: HTMLElement | undefined;
    let cssTag: HTMLElement | undefined;
    RootContainers.getRootContainer().style.display = shouldRender ? '' : 'none';

    if (shouldRender) {
      const targetRoute = [
        { path: CHANNEL_LIST_PATH, enabled: true },
        { path: SEARCH_VIEW_PATH, enabled: search },
        { path: SEARCH_PATH, enabled: search },
        { path: SINGLE_VIEW_PATH, enabled: true },
        { path: SHARE_VIEW_PATH, enabled: true },
        { path: SEARCH_AI_VIEW_PATH, enabled: search && genAI },
        { path: DEFAULT_VIEW_PATH, enabled: true },
        { path: CHANNEL_VIEW_PATH, enabled: true },
      ].find((route) => matchPath(route.path, location.pathname));

      if (!targetRoute?.enabled) {
        navigate(CHANNEL_LIST_PATH);
      }

      if (js) {
        jsTag = mountTag('script', {
          props: {
            text: `try{${js}}catch(e){console.log('js =>',e)}`,
          },
          attrs: {
            'data-custom-js': '',
          },
          appendContainer: RootContainers.getRootContainer().shadowRoot,
        });
      }

      if (css) {
        cssTag = mountTag('style', {
          props: {
            textContent: css,
          },
          attrs: {
            'data-custom-css': '',
          },
          appendContainer: RootContainers.getRootContainer().shadowRoot,
        });
      }
    }

    return () => {
      if (shouldRender) {
        unmountTag(jsTag);
        unmountTag(cssTag);
        store.dispatch(resetStoreAction());
      }
    };
  }, [shouldRender]);

  return shouldRender ? (
    <ErrorBoundary>
      <ThemeConfig>{props.children}</ThemeConfig>
    </ErrorBoundary>
  ) : (
    <div />
  );
}

export default GlobalTrigger;
